import styled, { css } from 'styled-components';
import { rem, ellipsis } from 'polished';
import mediaqueries from '../../../config/themes/variables/mediaqueries';

import Icon from '../Icon';
import ClickableTooltip from '../ClickableTooltip';

const StyledAccordionTable = styled.div`
  margin: 0 ${rem('20px')};
`;

const Section = styled.section`
  margin-bottom: ${rem('30px')};
  padding: 0 ${rem('20px')};
  ${mediaqueries.greaterThan('desktop')`
    display: flex;
    flex-flow: row wrap;
    padding: 0;
  `};
`;

const TitleSection = styled.h3`
  background-color: ${props => props.theme.colors.grayLightest};
  border: ${rem('1px')} solid ${props => props.theme.colors.grayLight};
  display: block;
  font-size: ${props => props.theme.fontSize.xsmall};
  margin: 0;
  width: 100%;
  position: relative;

  ${mediaqueries.greaterThan('desktop')`
    background: ${props => props.theme.colors.light};
    border: 0;
    border-bottom: ${rem('2px')} solid ${props => props.theme.colors.dark};
    font-family: ${props => props.theme.fonts.medium};
    font-size: ${props => props.theme.fontSize.small};
    text-transform: uppercase;
    flex-basis: 100%;
    display: flex;
    justify-content: space-between;    
  `};
`;

const TooltipElement = styled(ClickableTooltip)`
  padding: ${rem('1px')} ${rem('8px')};

  &:before,
  &:after {
    text-transform: none;
  }

  ${mediaqueries.greaterThan('desktop')`
    padding: ${rem('2px')} 0;
    cursor: initial;

    &:hover {
      cursor: initial;
    }

    &:before,
    &:after {
      content: none;
    }
  `};
`;

/**
 * data section
 */
const DataSection = styled.div`
  background-color: ${props => props.theme.colors.grayMoreLight};
  border: ${rem('1px')} solid ${props => props.theme.colors.grayLightest};

  ${props =>
    props.active &&
    `
    background-color: ${props.theme.colors.grayLightest};
    border-color: ${props.theme.colors.grayLight};
  `};

  ${mediaqueries.greaterThan('desktop')`
    align-items: center;
    background-color: ${props => props.theme.colors.grayMoreLight};
    border: 0;
    display: flex;
    flex: 0 calc(100% / 3);
    padding: 1rem;

    ${props =>
      props.span &&
      `
      flex-basis: calc(100% / (3 - ${props.span}));
    `}
  `};
`;

const HeaderData = styled.strong`
  outline: none;
  ${ellipsis()};

  align-items: baseline;
  cursor: pointer;
  display: flex;
  font-family: ${props => props.theme.fonts.medium};
  font-size: ${props => props.theme.fontSize.small};
  line-height: 1.3;
  padding: ${rem('14.9px')} ${rem('10px')};

  ${mediaqueries.greaterThan('desktop')`
    cursor: initial;
    display: inline;
    overflow: auto;
    white-space: normal;
    padding: 0;
    margin-right: 1rem;
  `};
`;

const IconArrow = styled(Icon)`
  margin-right: 0.625rem;
  transform: rotate(90deg);
  transition: none;
  min-width: ${rem('12px')};

  ${props =>
    props.active &&
    `
    fill: ${props.theme.colors.primary};
    transform: rotate(-90deg);
  `};

  ${mediaqueries.greaterThan('desktop')`
    display: none;
  `};
`;

const ElipsisSpan = styled.span`
  ${ellipsis()};
  font-family: inherit;
  font-size: inherit;

  ${props =>
    props.active &&
    css`
      white-space: normal;
      overflow: auto;
    `};

  ${mediaqueries.greaterThan('desktop')`
    display: inline;
    white-space: normal;
    overflow: auto;
  `};
`;

const BodyData = styled.span`
  display: block;
  font-size: 0;
  height: 0;
  line-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity 0.2s ease;
  visibility: hidden;

  ${props =>
    (props.active || props.withoutTitle) &&
    css`
      font-size: ${props => props.theme.fontSize.small};
      height: auto;
      line-height: 1.3;
      opacity: 1;
      padding: 0 ${rem('32px')} ${rem('10.5px')} ${rem('32px')};
      visibility: visible;
    `};

  ${props =>
    props.withoutTitle &&
    css`
      padding: ${rem('10.5px')} 0.5rem;
    `};

  ${mediaqueries.greaterThan('desktop')`
    font-size: ${props => props.theme.fontSize.small};
    height: auto;
    line-height: 1.3;
    opacity: 1;
    padding: 0;
    visibility: visible;
    display: inline;
  `};
`;

export {
  StyledAccordionTable,
  Section,
  TitleSection,
  TooltipElement,
  DataSection,
  HeaderData,
  IconArrow,
  ElipsisSpan,
  BodyData
};
